import { useState, type FormEvent } from "react";
import { useMe, useSession } from "../../app/session";
import { FormError } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { Checkbox } from "../../components/ui/Field";
import { useAction } from "../../hooks/useAction";
import { accountApi } from "../../lib/api/account";
import { isApiError } from "../../lib/api/http";
import { PRIVACY_URL, TERMS_URL } from "../../lib/site";
import { AuthLayout } from "./AuthLayout";

/**
 * Shown instead of the app to someone signed in who hasn't accepted the Terms in force: accounts made before the
 * sign-up box existed, and everyone once after the Terms change. Their streams keep running meanwhile.
 */
export function AcceptTermsPage() {
  const me = useMe();
  const { setMe, refresh, signOut } = useSession();
  const [agreed, setAgreed] = useState(false);

  const accept = useAction(async () => {
    try {
      setMe(await accountApi.acceptTerms(me.terms.version));
    } catch (e) {
      // The Terms changed while this page was open: load the new version, then ask again.
      if (isApiError(e, "TERMS_OUTDATED")) { setAgreed(false); await refresh(); }
      throw e;
    }
  });
  const submit = (e: FormEvent) => { e.preventDefault(); void accept.run(); };

  return (
    <AuthLayout title="Before you continue" footer={<Button size="sm" variant="ghost" onClick={() => void signOut()}>sign out</Button>}>
      <form onSubmit={submit} className="flex flex-col gap-4.5">
        <p className="text-muted">
          Please read and accept our Terms of Service and Privacy Policy to keep using Superlivestream. Your streams keep
          running while you do.
        </p>
        <Checkbox required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} label={
          <>I agree to the <a href={TERMS_URL} target="_blank" rel="noopener">Terms of Service</a> and <a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy Policy</a></>
        } />
        <FormError message={accept.error} />
        <Button type="submit" variant="primary" pending={accept.pending} pendingLabel="saving…">accept and continue →</Button>
      </form>
    </AuthLayout>
  );
}
